import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableSettings } from '../../../component';
import SvgIcoTableRowPlugsAfter from 'components/icon/icons/IcoTableRowPlusAfter';
import SvgIcoTableRowPlugsBefore from 'components/icon/icons/IcoTableRowPlusBefore';
import IcoHideElement from 'components/icon/icons/IcoHideElement';
import IcoShowElement from 'components/icon/icons/IcoShowElement';
import SvgIcoContentCopy from 'components/icon/icons/IcoContentCopy';

import { EditorDefaultSidePanelType, setActiveSidePanel } from '../../../../grid/reduxStore/blockStyleSettingsSlice';
import type { RootState } from '../../../../grid/reduxStore/Store';
import { useAppDispatch } from '../../../../grid/reduxStore/Store';
import { TotalRowTypesIdentifier } from 'components/editor/grid/reduxStore/table.types';
import { SelectionContext } from 'components/editor/GridDndEditor/SelectedBlockInfoProvider';
import { useSelector } from 'react-redux';
import { selectContentTable } from '../../../../grid/reduxStore/editorSlice';
import SvgIcoTrash from '../../../../../icon/icons/IcoTrash';
import { TableRowAddPositions, useTableRowManipulation } from '../RowSettings/useTableRowManipulation';
import { setOfRowsForPricingTable, setOfRowsForPricingTableWithReferences } from '../../../../GridDndEditor/Block/Table/defaultData';
import FormHelperText from '@mui/material/FormHelperText';
import useTableFooterRowTypeChange from './useTableFooterRowTypeChange';
import useFeatureFlag from '../../../../../../hooks/useFeatureFlag.ts';
import { FeatureFlags } from '../../../../../../utils/featureFlags.ts';

const FooterRowSettings = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { handleRowAdd, handleRowDuplication, handleDeleteTotalRow, isUpdating, handleHideSubtotalRow } = useTableRowManipulation();
  const { handleRowTypeChange } = useTableFooterRowTypeChange();
  const menuTitleTotalRows = t('document.grid.table.setting.total_rows');
  const subtotalRowHiddenTooltip = t('document.grid.table.setting.tooltip_hide_subtotal');
  const isCurrencySettingsEnabled = useFeatureFlag([FeatureFlags.currencySettings])[FeatureFlags.currencySettings];

  const { selectedBlockIdByWrapper, selectedSectionId } = useContext(SelectionContext);
  const sectionId = selectedSectionId as string;
  const blockId = selectedBlockIdByWrapper as string;
  const tableData = useSelector((state: RootState) => selectContentTable(state, sectionId, blockId)) || {};
  const { selectedRow } = tableData;
  const selectedRowType = selectedRow?.rowType;
  const isSelectedRowTypeTotal = selectedRowType === TotalRowTypesIdentifier.TOTAL;

  const isRowHidden = selectedRow?.isRowHidden ?? false;

  const rowButtons = [
    {
      icon: <SvgIcoTableRowPlugsAfter />,
      disabled: isSelectedRowTypeTotal || isUpdating,
      arialLabel: 'row-plus-after',
      fn: () => !isUpdating && handleRowAdd(TableRowAddPositions.AFTER),
    },
    {
      icon: <SvgIcoTableRowPlugsBefore />,
      disabled: isUpdating,
      arialLabel: 'row-plus-before',
      fn: () => !isUpdating && handleRowAdd(TableRowAddPositions.BEFORE),
    },
    {
      icon: <SvgIcoContentCopy />,
      disabled: isSelectedRowTypeTotal || isUpdating,
      arialLabel: 'content-copy',
      fn: () => !isUpdating && handleRowDuplication(),
    },
    {
      icon: <SvgIcoTrash />,
      disabled: isSelectedRowTypeTotal || isUpdating,
      arialLabel: 'trash',
      fn: () => !isUpdating && handleDeleteTotalRow(),
    },
  ];

  const subtotalRowButtons = [
    {
      icon: <SvgIcoTableRowPlugsAfter />,
      disabled: isUpdating,
      arialLabel: 'row-plus-after',
      fn: () => !isUpdating && handleRowAdd(TableRowAddPositions.AFTER),
    },
    {
      icon: <SvgIcoTableRowPlugsBefore />,
      disabled: true,
      arialLabel: 'row-plus-before',
      fn: () => !isUpdating && handleRowAdd(TableRowAddPositions.BEFORE),
    },
    {
      icon: <SvgIcoContentCopy />,
      disabled: true,
      arialLabel: 'content-copy',
      fn: () => !isUpdating && handleRowDuplication(),
    },
    {
      icon: isRowHidden ? <IcoHideElement data-testid="icon-hide" /> : <IcoShowElement data-testid="icon-show" />,
      arialLabel: 'hide-row',
      disabled: isUpdating,
      fn: () => !isUpdating && handleHideSubtotalRow(),
    },
  ];

  const activeRowButtons = selectedRowType === TotalRowTypesIdentifier.SUBTOTAL ? subtotalRowButtons : rowButtons;
  const selectOptions = {};
  const rowsForPricingTable = isCurrencySettingsEnabled ? setOfRowsForPricingTableWithReferences : setOfRowsForPricingTable;
  rowsForPricingTable.forEach((row) => (selectOptions[row.rowType.toLowerCase()] = row.rowType));
  const menuTitle = menuTitleTotalRows;
  const selectDisabled = useMemo(() => {
    const selectedRowType = selectedRow?.rowType;
    return selectedRowType === TotalRowTypesIdentifier.SUBTOTAL || selectedRowType === TotalRowTypesIdentifier.TOTAL || isUpdating;
  }, [selectedRow, isUpdating]);

  const handleBackButtonClick = () => {
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
  };

  const checkDisabled = (option: string): boolean => {
    if (option === TotalRowTypesIdentifier.SUBTOTAL) {
      return true;
    }
    return option === TotalRowTypesIdentifier.TOTAL;
  };

  return (
    <TableSettings
      iconButtons={activeRowButtons}
      handleBackButtonClick={handleBackButtonClick}
      handleSelectChange={handleRowTypeChange}
      helpText={t('document.grid.table.setting.update_select_row_type')}
      panelTitle={t('document.grid.table.setting.row_type')}
      selectId="select-row-type"
      selectOptions={selectOptions}
      checkDisabled={checkDisabled}
      selectValue={selectedRow?.rowType || ''}
      menuTitle={menuTitle}
      testId="table-row-settings"
      disabled={selectDisabled}
    >
      {isRowHidden && selectedRowType === TotalRowTypesIdentifier.SUBTOTAL && (
        <FormHelperText data-testid="subtotal-row-hidden-help-text">{subtotalRowHiddenTooltip}</FormHelperText>
      )}
    </TableSettings>
  );
};

export default FooterRowSettings;
